/**
 * useFlightAnimation — "fly to bell" effect for task-launching buttons.
 *
 * When a long-running task is kicked off, call fire(buttonElement) to spawn a
 * semi-transparent ghost of the button that shrinks toward the notification bell,
 * giving the user a visual cue that work has been queued there.
 *
 * Automatically skips the animation when prefers-reduced-motion is set.
 */

import { useCallback } from 'react';

const BELL_SELECTOR = 'button[title="Tasks"]';
const FLIGHT_MS = 680;

export function useFlightAnimation() {
  const fire = useCallback((trigger: HTMLElement | null) => {
    if (!trigger) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const bell = document.querySelector<HTMLElement>(BELL_SELECTOR);
    if (!bell) return;

    const from = trigger.getBoundingClientRect();
    const to = bell.getBoundingClientRect();

    // Center-to-center offset so the ghost vanishes into the middle of the bell
    const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
    const dy = (to.top + to.height / 2) - (from.top + from.height / 2);

    // Clone button appearance as a fixed-position ghost
    const ghost = document.createElement('div');
    ghost.className = 'task-fly-ghost';
    ghost.textContent = trigger.textContent;
    ghost.style.left = `${from.left}px`;
    ghost.style.top = `${from.top}px`;
    ghost.style.width = `${from.width}px`;
    ghost.style.height = `${from.height}px`;
    ghost.style.setProperty('--fly-dx', `${dx}px`);
    ghost.style.setProperty('--fly-dy', `${dy}px`);
    document.body.appendChild(ghost);

    // Ring the bell just as the ghost arrives
    setTimeout(() => {
      bell.classList.add('bell-ring');
      setTimeout(() => bell.classList.remove('bell-ring'), 520);
    }, FLIGHT_MS - 80);

    // Clean up ghost after animation completes
    setTimeout(() => ghost.remove(), FLIGHT_MS + 100);
  }, []);

  return { fire };
}
